// #genai Guest access service layer — all API calls for the shared-resource / guest-user flow.
// Each function returns { ok, json, status } for consistent error handling throughout the app.
//
// Token strategy
// ──────────────
// The pre-access token (requestPreAccessToken) is a short-lived, narrow-purpose guest service token.
// It is stored under the dedicated key PRE_ACCESS_TOKEN_KEY ('ev_pre_access_token') in
// localStorage — it NEVER touches the main 'token' key which holds the user's
// Okta / custom-auth token.
//
// sendEmailVerification, pollEmailVerification, and verifyGuestEmailToken are guest-user-service
// endpoints that require this pre-access token. They receive it via an explicit
// "Authorization: Bearer <token>" header built by withPreAccessAuth() below — the global
// fetch interceptor is NOT involved.
import {
  VALIDATE_RESOURCE_PATH_API,
  GUEST_ACCESS_RATE_LIMIT_URL,
  GUEST_EMAIL_VERIFICATION_API,
  GUEST_POLL_EMAIL_VERIFICATION_API,
  VERIFY_GUEST_EMAIL_TOKEN_API,
  CHECK_EV_USER_API,
  GUEST_RESOURCE_ACCESS_API,
  RESOURCE_ACCESS_AUDIT_API,
  PRE_ACCESS_TOKEN_KEY,
} from '../constants/AppConstants';
import { calculateExpiryTime } from '../utils/app.utils';

// ─────────────────────────────────────────────
// Internal helpers
// ─────────────────────────────────────────────

/**
 * Wraps a fetch promise and normalises the response into { ok, json, status }.
 */
const normalise = (fetchPromise) => fetchPromise
  .then(async (response) => {
    try {
      const json = await response.json();
      return { ok: response.ok, json, status: response.status };
    } catch (_) {
      return { ok: response.ok, json: {}, status: response.status };
    }
  })
  .catch((error) => ({ ok: false, json: { errorDescription: error.message }, status: 0 }));

/**
 * Reads the pre-access token stored by SharedStartupGate from its dedicated key.
 * Returns an empty string when no token is present (e.g. before the gate has run).
 */
const getPreAccessToken = () => localStorage.getItem(PRE_ACCESS_TOKEN_KEY) || '';

/**
 * Builds request headers for guest-user-service calls that require the pre-access token.
 * Merges the Authorization header in only when a token is available.
 * @param {object} [extra={}] - Additional headers to merge in.
 * @returns {object}
 */
const withPreAccessAuth = (extra = {}) => {
  const token = getPreAccessToken();
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
    ...extra,
  };
};

// ─────────────────────────────────────────────
// Public helpers for token lifecycle
// ─────────────────────────────────────────────

/**
 * Persists the pre-access token to its dedicated localStorage key.
 * Called by SharedStartupGate after a successful requestPreAccessToken response.
 * Writing here does NOT affect the user's 'token' entry.
 * @param {string} token
 */
export const storePreAccessToken = (token) => {
  if (!token) return;
  try {
    localStorage.setItem(PRE_ACCESS_TOKEN_KEY, token);
  } catch (_) {
    // noop — non-blocking; if storage is unavailable the auth headers will just be missing
  }
};

/**
 * Removes the pre-access token from localStorage.
 * Called automatically on logout via LOCAL_STORAGE_ITEMS; can also be called
 * explicitly when the guest flow completes successfully.
 */
export const clearPreAccessToken = () => {
  localStorage.removeItem(PRE_ACCESS_TOKEN_KEY);
};

// ─────────────────────────────────────────────
// validateResourcePath
// GET /viewpoint-service/v1/share/apps/{appCode}/validate/resource-path?resourcePath=...
// Returns { valid: boolean, resourceShareId?: string, ... }
// Does NOT require the pre-access token.
// skipAuthHeader: true — unauthenticated; the fetch interceptor must not inject any
// user JWT (Okta or guest) or the backend will reject the request. #genai
// ─────────────────────────────────────────────

/**
 * Checks whether a given resource path is marked as shared on the platform.
 * @param {string} appCode      - First path segment of the current URL (e.g. "property")
 * @param {string} resourcePath - Full resource path (e.g. "apps.eagleview.com/property/123")
 * @returns {Promise<{ ok, json, status }>}
 */
export const validateResourcePathService = (appCode, resourcePath) => {
  const url = `${VALIDATE_RESOURCE_PATH_API}/${appCode}/validate/resource-path?resourcePath=${encodeURIComponent(resourcePath)}`;
  return normalise(fetch(url, { method: 'GET', skipAuthHeader: true }));
};

// ─────────────────────────────────────────────
// requestPreAccessToken
// POST /share-service/v1/share/apps/{appCode}/request-token
// Body:    { resourcePath: string }
// Returns: {
//   preAccessToken: string,
//   resourceShareId: string,
//   appCode: string,
//   resourceId: string,
//   isResourceShared: boolean
// }
// Does NOT require the pre-access token (this call creates it).
// skipAuthHeader: true — unauthenticated; the fetch interceptor must not inject any
// user JWT (Okta or guest) or the backend will reject the request. #genai
// ─────────────────────────────────────────────

/**
 * Obtains a short-lived pre-access token for subsequent guest-user-service calls
 * and confirms whether the resource is marked as shared.
 * @param {string} appCode
 * @param {string} resourcePath
 * @returns {Promise<{ ok, json, status }>}
 */
export const requestPreAccessTokenService = (appCode, resourcePath) => {
  const url = `${GUEST_ACCESS_RATE_LIMIT_URL}/share/apps/${appCode}/request-token`;
  return normalise(
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ resourcePath }),
      skipAuthHeader: true,
    }),
  );
};

// ─────────────────────────────────────────────
// checkIsEvUser
// POST /guest-user-service/v1/share/resource-share
// Headers: Authorization: Bearer <preAccessToken>
// Body:    { emailId: string }
// Returns: { evUser: boolean, loginId: string }   ← success
//          { errorCode, errorDescription }         ← failure
//
// The backend field is `evUser`; we normalise it to `isEvUser` here so
// Redux state (GuestAccess.isEvUser) and all call-sites remain consistent.
// The loginId returned on success is passed as the body of sendEmailVerification.
// ─────────────────────────────────────────────

/**
 * Checks whether an email address belongs to an existing EagleView user.
 * Requires the pre-access token stored by SharedStartupGate.
 *
 * Normalises the backend response field `evUser` → `isEvUser` so that
 * downstream consumers (SignInFlow, GuestAccess Redux) do not need to
 * know about the raw API field name.
 *
 * @param {string} email - Email address entered by the user on /signin
 * @returns {Promise<{ ok: boolean, json: { isEvUser: boolean, loginId: string }, status: number }>}
 * #genai
 */
export const checkIsEvUserService = async (email = '') => {
  const result = await normalise(
    fetch(CHECK_EV_USER_API, {
      method: 'POST',
      headers: withPreAccessAuth(),
      body: JSON.stringify({ emailId: email }),
    }),
  );
  // Remap evUser → isEvUser for consistent Redux state naming
  const { evUser, loginId, ...rest } = result.json || {};
  return {
    ok: result.ok,
    status: result.status,
    json: { ...rest, isEvUser: Boolean(evUser), loginId: loginId || null },
  };
};

// ─────────────────────────────────────────────
// sendEmailVerification
// POST /guest-user-service/v1/share/email-verifications
// Headers: Authorization: Bearer <preAccessToken>
// Body:    { loginId: string }
// Returns: { verificationId: string, ... }
// ─────────────────────────────────────────────

/**
 * Triggers a guest sign-in email to be sent to the user.
 * The returned verificationId is used by pollEmailVerification.
 * Requires the pre-access token stored by SharedStartupGate.
 * @param {string} loginId - UUID from the isEvUser API (or email as stub fallback)
 * @returns {Promise<{ ok, json, status }>}
 */
export const sendEmailVerificationService = (loginId) => normalise(
  fetch(GUEST_EMAIL_VERIFICATION_API, {
    method: 'POST',
    headers: withPreAccessAuth(),
    body: JSON.stringify({ loginId }),
  }),
);

// ─────────────────────────────────────────────
// pollEmailVerification
// GET /guest-user-service/v1/share/email-verifications/{emailVerificationId}
// {emailVerificationId} is the value returned by sendEmailVerification.
// Headers: Authorization: Bearer <preAccessToken>
// Returns: { status: 'pending'|'VERIFIED', customAccessToken?: { access_token, expires_in, scope, token_type } }
// Cross-origin: credentials: 'include' so the browser allows the response and cookies.
// Backend must use HTTPS for Set-Cookie (Secure; SameSite=None) to be accepted by the browser. #genai
// ─────────────────────────────────────────────

/**
 * Polls whether the user has clicked the magic link in their inbox.
 * First poll fires after 20 s; subsequent polls every 5 s (up to 10 min).
 * On success, the response includes a `customAccessToken` object (OAuth-style) to store as the user session.
 * @param {string} verificationId - emailVerificationId returned by sendEmailVerification
 * @returns {Promise<{ ok, json, status }>}
 */
export const pollEmailVerificationService = (verificationId) => {
  const url = `${GUEST_POLL_EMAIL_VERIFICATION_API}/${verificationId}`;
  return normalise(
    fetch(url, {
      method: 'GET',
      credentials: 'include',
      headers: withPreAccessAuth(),
    }),
  );
};

// ─────────────────────────────────────────────
// verifyGuestEmailToken
// POST /guest-user-service/v1/share/email-verifications/verify?t=TOKEN
// skipAuthHeader: true  — the magic-link token in ?t= is the only credential needed;
//                         the interceptor must not inject any user JWT.
// skip401Handling: true — a 401 from this endpoint should not trigger the global
//                         sign-out / redirect flow. #genai
// Called from the /verify page when the user clicks the link in their inbox.
// This marks the session as verified on the backend so the pollEmailVerification loop
// in the sign-in tab receives the customAccessToken.
// ─────────────────────────────────────────────

/**
 * Confirms the magic-link token from the guest sign-in email.
 * @param {string} token - Raw `t` query-param value from the magic link URL
 * @returns {Promise<{ ok, json, status }>}
 */
export const verifyGuestEmailTokenService = (token) => {
  const url = `${VERIFY_GUEST_EMAIL_TOKEN_API}?t=${encodeURIComponent(token)}`;
  return normalise(
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      skipAuthHeader: true,
      skip401Handling: true,
    }),
  );
};

/**
 * GET /share/apps/{appCode}/resource/{resourceId}/access — returns { hasAccess: boolean, resourceAccessAuditId: string }.
 * Accepts both Okta and customAuth bearer tokens. #genai
 *
 * @param {string} appCode
 * @param {string} resourceId
 * @param {string} authToken
 * @returns {Promise<{ ok: boolean, json: { hasAccess: boolean, resourceAccessAuditId: string }, status: number }>}
 */
export const checkResourceAccessService = (appCode, resourceId, authToken) => {
  const url = `${GUEST_RESOURCE_ACCESS_API}/${appCode}/resource/${encodeURIComponent(resourceId)}/access`;
  return normalise(
    fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(authToken && { Authorization: `Bearer ${authToken}` }),
      },
      skip401Handling: true,
    }),
  );
};

// ─────────────────────────────────────────────
// Shared helpers — reused by SharedStartupGate, SignInFlow, and the polling hook
// ─────────────────────────────────────────────

/**
 * Processes the raw results of validateResourcePath API and requestPreAccessToken API
 * and returns a normalised payload ready for Redux dispatch.
 */
export const processBootstrapResults = (validationResult, preAccessResult, fallbackAppCode, resourcePath) => {
  const pathIsValid = Boolean(validationResult.ok && validationResult.json?.valid === true);
  const isPreAccessTokenOk = Boolean(preAccessResult.ok);
  const apiConfirmsShared = Boolean(preAccessResult.json?.isResourceShared === true);
  const isResourceShared = pathIsValid && apiConfirmsShared;

  return {
    isResourceShared,
    isPreAccessTokenOk,
    preAccessToken: preAccessResult.json?.preAccessToken || '',
    resourceShareId: preAccessResult.json?.resourceShareId || '',
    resourceId: preAccessResult.json?.resourceId || '',
    appCode: preAccessResult.json?.appCode || fallbackAppCode,
    resourcePath,
  };
};
/**
 * Calls checkResourceAccessService and returns { hasAccess, resourceAccessAuditId }.
 * Fails open (hasAccess: true) on network/parse errors so the user is never blocked by a flaky API.
 * resourceAccessAuditId is null when the API fails or omits the field.
 */
export const checkAccessWithFallback = async (appCode, resourceId, authToken) => {
  try {
    const { ok, json } = await checkResourceAccessService(appCode, resourceId, authToken);
    return ok
      ? { hasAccess: Boolean(json.hasAccess), resourceAccessAuditId: json.resourceAccessAuditId || null }
      : { hasAccess: true, resourceAccessAuditId: null };
  } catch (_) {
    return { hasAccess: true, resourceAccessAuditId: null };
  }
};

/**
 * PUT /v1/share/resource-access-audit/{resourceAccessAuditId}
 * Fire-and-forget call to update the access status of a resource access audit record.
 * Uses the same auth token as checkResourceAccess (Okta token for EV users).
 *
 * @param {string} resourceAccessAuditId
 * @param {string} accessStatus - One of: 'ALLOWED', 'DENIED'
 * @param {string} authToken - Bearer token (Okta or custom-auth)
 * @returns {Promise<{ ok, json, status }>}
 */
export const updateResourceAccessAuditStatusService = (resourceAccessAuditId, accessStatus, authToken) => {
  const url = `${RESOURCE_ACCESS_AUDIT_API}/${encodeURIComponent(resourceAccessAuditId)}`;
  return normalise(
    fetch(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...(authToken && { Authorization: `Bearer ${authToken}` }),
      },
      body: JSON.stringify({ accessStatus }),
      skip401Handling: true,
    }),
  );
};

/**
 * Extracts the email verification ID from a sendEmailVerification response,
 * trying the known field names in priority order.
 */
export const extractVerificationId = (json) => json.emailVerificationId || json.verificationId || json.id || '';

/**
 * Builds the token object that `setAuthToken` (and the rest of the app) expects.
 * The polling API may return just a raw token string or a full object.
 *
 * Computes `expiry_time` from `expires_in` when it is present so that
 * `isTokenValid()` returns true for guest tokens after a full-page navigation
 * (isTokenValid requires expiry_time — without it the guest is treated as
 * unauthenticated and SharedStartupGate redirects back to /signin). #genai
 */
export const buildTokenPayload = (raw) => {
  if (raw && typeof raw === 'object' && raw.access_token) {
    if (raw.expires_in && !raw.expiry_time) {
      return { ...raw, expiry_time: calculateExpiryTime(raw.expires_in) };
    }
    return raw;
  }
  return { access_token: typeof raw === 'string' ? raw : '' };
};
